import { z } from "zod";
import { AppUser } from "@/api/adapters/patients";
import { isNotFutureDate } from "@/shared/utils/date-validation";

// ── Guardian relationship options ──
export const GUARDIAN_RELATIONSHIPS = [
	"SPOUSE",
	"CHILD",
	"PARENT",
	"SIBLING",
	"OTHER",
] as const;


// ── Zod schema ──
// 공통 00 §2.1: 휴대폰은 010으로 시작하는 11자리 숫자만 허용(대시·기타 접두 불가). BE canonicalPhone(^010\d{8}$)과 정합.
const phonePattern = /^010\d{8}$/;
// 환자 이름 = 공통 00 §2.5 한글 2~20자 (확정 2026-07-10, §8 부록 "환자 이름 (03) 한글 2~20 ✅확정" 일치).
// 이 규칙은 환자 이름 전용이다 — 보호자 이름에 전용하지 말 것. 보호자는 아래 NAME 클래스를 따른다.
// 참고: BE FEATURE_014는 아직 2~50자·영문 허용(^[가-힣a-zA-Z ]+$)이라 FE가 더 엄격하다. FE↔BE 재동기 필요.
const namePattern = /^[가-힣]{2,20}$/;
// SHCT-543 — 보호자 이름은 "이름 규칙" 대상이 아니라 NAME 클래스다. 공통 00 §3이 보호자 이름을
// NAME(이름 규칙 아님·자유텍스트)으로 배정하고 길이를 1~50으로 정의하며, §8 부록의 두 행(환자명·보호자명 (02),
// 보호자 이름·환자와의 관계 (03))과 의료진웹 PRD 03 §4.2("NAME 클래스(1~50자)")가 모두 같은 판정을 내린다.
// 허용 문자는 §4.2 매트릭스의 NAME 행 = 기본 문자(한글·영문·숫자·공백) + 일반 특수문자, 이모지 ❌, 줄바꿈 ❌.
// 그래서 화이트리스트 정규식이 아니라 이모지·줄바꿈만 거르는 방식이다 — 한글만 허용하는 패턴을 쓰면
// 스펙이 명시적으로 허용한 영문·숫자·공백·특수문자를 되레 막게 된다.
const NAME_CLASS_MAX = 50;
const EMOJI_RE = /\p{Extended_Pictographic}/u;
const hasEmoji = (v: string) => EMOJI_RE.test(v);
const hasLineBreak = (v: string) => /[\r\n]/.test(v);
// 공통 00 §2.7: 등록번호(MRN) = 영문·숫자·대시(-), 최대 20자(최소 길이 제한 없음).
const mrnPattern = /^[A-Za-z0-9-]{1,20}$/;

export const patientRegistrationSchema = z
	.object({
		// App user toggle
		appUser: z.enum(Object.values(AppUser) as [string, ...string[]]),

		// Patient fields (all modes)
		name: z
			.string()
			.min(1, 'validation:required')
			.min(2, 'validation:name.minLength')
			.max(20, 'validation:name.maxLength')
			.regex(namePattern, 'validation:name.korean'),
		mrn: z
			.string()
			.min(1, 'validation:required')
			.regex(mrnPattern, 'validation:mrn.invalid'),
		dischargeDate: z.string().min(1, 'validation:required'),
		primaryDx: z.string().min(1, 'validation:required'),
		secondaryDx: z.string().optional(),
		// Prescription rows: each is one drug paired with how often it is taken, and the
		// clinician can add or remove rows. Sent to the server as this list — it stores a
		// row per drug, so which interval belongs to which drug survives.
		medications: z
			.array(
				z.object({
					name: z.string().max(50, 'validation:common.maxLength'),
					freq: z.string().max(50, 'validation:common.maxLength'),
				}),
			)
			.optional(),

		// Patient phone (required in patient mode, optional in guardian mode)
		patientPhone: z.string().optional(),

		// Guardian-only fields
		birthDate: z.string().optional(),
		gender: z.string().optional(),
		guardianName: z.string().optional(),
		guardianRelationship: z.string().optional(),
		guardianPhone: z.string().optional(),
		/** Staff ids assigned to the patient (PRD §3.2). Built at submit time from assignedStaff, not registered as a form field. */
		careTeam: z.array(z.string()).optional(),
	})
	.superRefine((data, ctx) => {
		// 퇴원일은 오늘 이전이어야 한다 (PRD §4.2 / §8, PRO 기준일).
		if (data.dischargeDate && !isNotFutureDate(data.dischargeDate)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'validation:date.future',
				path: ['dischargeDate'],
			});
		}

		if (data.appUser === AppUser.PATIENT) {
			if (!data.patientPhone || data.patientPhone.trim() === '') {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'validation:required',
					path: ['patientPhone'],
				});
			} else if (!phonePattern.test(data.patientPhone)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'validation:phone.invalid010',
					path: ['patientPhone'],
				});
			}
		}

		if (data.appUser === AppUser.GUARDIAN) {
			if (!data.birthDate || data.birthDate.trim() === '') {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'validation:required',
					path: ['birthDate'],
				});
			} else if (!isNotFutureDate(data.birthDate)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'validation:date.future',
					path: ['birthDate'],
				});
			}
			if (!data.gender || data.gender.trim() === '') {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'validation:required',
					path: ['gender'],
				});
			}
			// 보호자 이름 = NAME 클래스 1~50자, 이모지·줄바꿈만 불허 (공통 00 §3·§4.2, PRD 03 §4.2).
			// 최소 길이는 1이다 — 환자 이름의 2자 하한은 §2.5 이름 규칙에 속하고 NAME 클래스에는 없다.
			const guardianName = (data.guardianName ?? '').trim();
			if (guardianName === '') {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'validation:required',
					path: ['guardianName'],
				});
			} else {
				if (guardianName.length > NAME_CLASS_MAX) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'validation:name.guardianMaxLength',
						path: ['guardianName'],
					});
				}
				if (hasEmoji(guardianName)) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'validation:common.noEmoji',
						path: ['guardianName'],
					});
				}
				if (hasLineBreak(guardianName)) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'validation:common.noLineBreak',
						path: ['guardianName'],
					});
				}
			}
			if (
				!data.guardianRelationship ||
				data.guardianRelationship.trim() === ''
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'validation:required',
					path: ['guardianRelationship'],
				});
			}
			if (!data.guardianPhone || data.guardianPhone.trim() === '') {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'validation:required',
					path: ['guardianPhone'],
				});
			} else if (!phonePattern.test(data.guardianPhone)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'validation:phone.invalid010',
					path: ['guardianPhone'],
				});
			}
		}
	});

export type PatientRegistrationFormValues = z.infer<
	typeof patientRegistrationSchema
>;

// Helper: field names that are conditional on appUser type
export const GUARDIAN_ONLY_FIELDS = [
	"birthDate",
	"gender",
	"guardianName",
	"guardianRelationship",
	"guardianPhone",
] as const;
